import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, Row, Col, Card, Checkbox } from "antd";
import {
  vehicleMakes,
  getYears,
  getModelsForMake,
} from "../../../_helpers/vehicleData";
import { FormatPhone, formatMoney } from "_assets/js/helpers";
import bgImage2 from "../../../_assets/images/sky-bg-2.jpg";
import bgImage from "../../../_assets/images/sku-bg.jpg";
import { useNavigate } from "react-router-dom";
import "./ApplicationForm.scss";

const { Option } = Select;

const ApplicationForm = (props) => {
  const navigate = useNavigate();
  const [formType, setFormType] = useState(undefined);
  const [form] = Form.useForm();
  const [models, setModels] = useState([]);
  const [submitted, setSubmitted] = useState(props.isThanks);

  // Update submitted state when isThanks prop changes
  useEffect(() => {
    setSubmitted(props.isThanks);
  }, [props.isThanks]);

  const actualSource =
    process.env.REACT_APP_SIMPLE === "apply" ? "LPA - Apply" : "LPA - ApplyNow";

  const onFinish = async (values) => {
    try {
      let postBody = values;
      postBody = {
        ...values,
        source: actualSource,
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/application/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(postBody),
        }
      );
      if (response.ok) {
        navigate("/thank-you");
      } else {
        // Optionally handle error
        console.error("Submission failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleFormTypeChange = (value) => {
    setFormType(value);
    form.resetFields(["vin", "make", "model", "year"]);
    setModels([]);
  };

  const handleMakeChange = (value) => {
    const selectedModels = getModelsForMake(value);
    setModels(selectedModels);
    form.setFieldsValue({ model: undefined });
  };

  const years = getYears();

  // Only allow numeric input for year
  const handleYearChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, "");
    form.setFieldsValue({ year: value });
  };

  // Format phone as 555-555-5555 as user types
  const handlePhoneChange = (e) => {
    const formatted = FormatPhone(e.target.value);
    form.setFieldsValue({ phone: formatted });
  };

  const handleAmountChange = (e) => {
    const numeric = e.target.value.replace(/[^\d]/g, "");
    const formatted = formatMoney(numeric)
      .replace(/\$|\.00/g, "")
      .trim();
    form.setFieldsValue({ amount: formatted });
  };

  // Format miles with commas as user types, but keep value numeric
  const handleMilesChange = (e) => {
    const numeric = e.target.value.replace(/[^\d]/g, "");
    // Remove $ and .00 from formatMoney output
    const formatted = formatMoney(numeric)
      .replace(/\$|\.00/g, "")
      .trim();
    form.setFieldsValue({ miles: formatted });
  };

  const whichBackground = () => {
    if (process.env.REACT_APP_SIMPLE === "apply") {
      return bgImage;
    } else {
      return bgImage2;
    }
  };

  return (
    <div
      className="application-form-container"
      style={{ backgroundImage: `url(${whichBackground()})` }}
    >
      <div className="form-content">
        <Card className="form-card">
          {submitted ? (
            <div className="success-message">
              <h2>Thank you for submitting!</h2>
              <p>We will be in touch soon.</p>
            </div>
          ) : (
            <Form
              form={form}
              name="auto_loan_application"
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ formType: undefined }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your first name",
                      },
                    ]}
                  >
                    <Input size="large" placeholder="Enter your first name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your last name",
                      },
                    ]}
                  >
                    <Input size="large" placeholder="Enter your last name" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input size="large" placeholder="Enter your email" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your phone number",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter your phone number"
                      onChange={handlePhoneChange}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="formType"
                label="VIN or Model"
                rules={[{ required: false }]}
              >
                <Select
                  size="large"
                  onChange={handleFormTypeChange}
                  placeholder="-- None --"
                >
                  <Option value="vin">I have a VIN number</Option>
                  <Option value="complete">Complete all fields</Option>
                </Select>
              </Form.Item>

              {formType === "vin" ? (
                <Row gutter={16}>
                  <Col xs={24} sm={16}>
                    <Form.Item
                      name="vin"
                      label="VIN Number"
                      rules={[
                        {
                          required: true,
                          message: "Please enter the VIN number",
                        },
                      ]}
                    >
                      <Input size="large" placeholder="Enter the VIN number" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="miles"
                      label="Miles"
                      rules={[
                        { required: true, message: "Please enter the mileage" },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="Enter the mileage"
                        onChange={handleMilesChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null}
              {formType === "complete" ? (
                <>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="make"
                        label="Make"
                        rules={[
                          { required: true, message: "Please select the make" },
                        ]}
                      >
                        <Select
                          placeholder="Select make"
                          onChange={handleMakeChange}
                          size="large"
                        >
                          {vehicleMakes.map((make) => (
                            <Option key={make} value={make}>
                              {make}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="model"
                        label="Model"
                        rules={[
                          {
                            required: true,
                            message: "Please select the model",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select model"
                          disabled={models.length === 0}
                          size="large"
                        >
                          {models.map((model) => (
                            <Option key={model} value={model}>
                              {model}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="year"
                        label="Year"
                        rules={[
                          { required: true, message: "Please enter the year" },
                          {
                            pattern: /^\d{4}$/,
                            message: "Year must be a 4-digit number",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter year"
                          maxLength={4}
                          inputMode="numeric"
                          onChange={handleYearChange}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="miles"
                        label="Miles"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the mileage",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter the mileage"
                          onChange={handleMilesChange}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ) : null}

              <Form.Item
                name="amount"
                label="Amount Requested"
                rules={[
                  {
                    required: true,
                    message: "Please enter the amount requested",
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter the amount requested"
                  onChange={handleAmountChange}
                />
              </Form.Item>

              <Form.Item
                name="terms"
                valuePropName="checked"
                rules={[
                  {
                    required: true,
                    message: "You must agree to the terms to submit.",
                  },
                ]}
              >
                <Checkbox>
                  I understand that, by checking this box and clicking the
                  "Submit" button, I am intending to sign this record
                  authorizing Sky Auto Finance LLC to deliver telemarketing
                  calls, text messages, the use of an auto dialer and
                  prerecorded messages to the cell number I have provided. I
                  also agree to the terms of our{" "}
                  <a
                    href="https://skyautofinance.com/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>{" "}
                  and understand that I am not required to provide this
                  authorization as a condition of doing business with Sky Auto
                  Finance LLC. I may revoke my consent at any time.
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="submit-button"
                >
                  Submit Application
                </Button>
              </Form.Item>
            </Form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ApplicationForm;
